import React, { useState } from 'react';
import { ViewState, Language } from '../types';
import { TRANSLATIONS } from '../constants';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard, Map, BrainCircuit, Users,
  ShieldAlert, Activity, Menu, X, Globe,
  Hexagon
} from 'lucide-react';

interface LayoutProps {
  currentView: ViewState;
  setView: (view: ViewState) => void;
  children: React.ReactNode;
  lang: Language;
  setLang: (lang: Language) => void;
  onOpenReport: () => void;
  onOpenPulse: () => void;
  onOpenAngel: () => void;
  isSidebarOpen: boolean;
  onToggleSidebar: () => void;
}

const Layout: React.FC<LayoutProps> = ({
  currentView,
  setView,
  children,
  lang,
  setLang,
  onOpenReport,
  onOpenPulse,
  onOpenAngel,
  isSidebarOpen,
  onToggleSidebar
}) => {
  const t = TRANSLATIONS[lang];

  const navItems = [
    { id: ViewState.DASHBOARD, label: 'Dashboard', icon: LayoutDashboard },
    { id: ViewState.COMMAND_CENTER, label: 'Command Center', icon: Map },
    { id: ViewState.PULSE, label: 'Pulse Link', icon: Activity },
    { id: ViewState.INTEL_CORTEX, label: 'Intel Cortex', icon: BrainCircuit },
    { id: ViewState.CONTACTS, label: 'Network', icon: Users },
  ];

  return (
    <div className="flex h-screen w-full bg-[var(--bg-deepest)] text-[var(--text-primary)] overflow-hidden font-sans selection:bg-blue-500/30">

      {/* BACKGROUND EFFECTS */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <div className="absolute top-[-20%] left-[-10%] w-[50%] h-[50%] bg-blue-600/5 rounded-full blur-[120px]" />
        <div className="absolute bottom-[-20%] right-[-10%] w-[50%] h-[50%] bg-indigo-600/5 rounded-full blur-[120px]" />
        <div className="absolute inset-0 opacity-[0.03]"
          style={{ backgroundImage: 'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)', backgroundSize: '40px 40px' }}
        />
      </div>

      {/* SIDEBAR NAVIGATION (Desktop) */}
      <aside className="hidden md:flex flex-col w-64 h-full border-r border-white/5 bg-[var(--bg-deep)]/90 backdrop-blur-xl z-20 shadow-[5px_0_30px_rgba(0,0,0,0.2)]">
        <div className="p-6 flex items-center gap-3">
          <div className="relative group">
            <Hexagon className="w-8 h-8 text-[var(--neon-blue)] fill-blue-500/10 group-hover:rotate-180 transition-transform duration-700" />
            <div className="absolute inset-0 flex items-center justify-center font-bold text-[10px] text-[var(--neon-cyan)] animate-pulse">LN</div>
          </div>
          <div>
            <h1 className="text-xl font-display font-bold tracking-wider text-white">Protectly</h1>
            <p className="text-[10px] text-[var(--neon-cyan)] tracking-[0.2em]">DIGITAL GUARDIAN</p>
          </div>
        </div>

        <nav className="flex-1 px-4 space-y-2 mt-4">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = currentView === item.id;
            return (
              <button
                key={item.id}
                onClick={() => setView(item.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 group relative overflow-hidden ${isActive
                  ? 'text-white shadow-[0_0_20px_rgba(37,99,235,0.2)]'
                  : 'text-gray-400 hover:text-white'
                  }`}
              >
                {isActive && (
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-transparent border-l-2 border-blue-500" />
                )}
                <div className={`relative z-10 p-1.5 rounded-lg transition-colors ${isActive ? 'bg-blue-500/20 text-blue-400' : 'bg-transparent group-hover:bg-white/5'}`}>
                  <Icon className={`w-5 h-5 ${isActive ? 'animate-pulse' : ''}`} />
                </div>
                <span className={`relative z-10 font-medium tracking-wide text-sm ${isActive ? 'font-bold' : ''}`}>{item.label}</span>
              </button>
            );
          })}

          <div className="my-6 border-t border-white/5 mx-2" />

          <div className="px-2">
            <p className="text-[10px] font-bold text-gray-500 mb-3 px-2 uppercase tracking-widest">Quick Actions</p>
            <button onClick={onOpenReport} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-gray-400 hover:bg-red-500/10 hover:text-red-400 hover:border hover:border-red-500/20 transition-all group mb-2 border border-transparent">
              <ShieldAlert className="w-5 h-5 group-hover:animate-bounce" />
              <span className="text-sm font-bold">Report Hazard</span>
            </button>
            <button onClick={onOpenPulse} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-gray-400 hover:bg-green-500/10 hover:text-green-400 hover:border hover:border-green-500/20 transition-all group border border-transparent">
              <Activity className="w-5 h-5 group-hover:animate-pulse" />
              <span className="text-sm font-bold">Quick Pulse</span>
            </button>
          </div>
        </nav>

        <div className="p-4 border-t border-white/5 bg-black/20">
          <button onClick={() => setLang(lang === 'en' ? 'ar' : 'en')} className="flex items-center gap-2 text-xs text-gray-500 hover:text-white transition-colors w-full justify-center py-2 hover:bg-white/5 rounded-lg">
            <Globe className="w-3 h-3" />
            <span>Language: {lang === 'en' ? 'English' : 'العربية'}</span>
          </button>
        </div>
      </aside>

      {/* MOBILE SIDEBAR OVERLAY */}
      <AnimatePresence>
        {isSidebarOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={onToggleSidebar}
              className="fixed inset-0 bg-black/80 z-[90] md:hidden backdrop-blur-sm"
            />
            <motion.div
              initial={{ x: '-100%' }} animate={{ x: 0 }} exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed top-0 left-0 bottom-0 w-[280px] bg-[#020817] border-r border-blue-500/20 z-[100] md:hidden flex flex-col shadow-[0_0_50px_rgba(0,0,0,0.5)]"
            >
              <div className="p-6 flex items-center justify-between border-b border-white/5 bg-gradient-to-r from-blue-900/10 to-transparent">
                <span className="font-display font-bold text-lg tracking-widest text-white">MENU</span>
                <button onClick={onToggleSidebar} className="p-2 hover:bg-white/5 rounded-full text-gray-400 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-2">
                {navItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => { setView(item.id); onToggleSidebar(); }}
                    className={`w-full flex items-center gap-4 px-4 py-4 rounded-xl text-left border ${currentView === item.id ? 'bg-blue-600/10 border-blue-500/50 text-blue-400' : 'border-transparent text-gray-400 hover:bg-white/5'
                      }`}
                  >
                    <item.icon className="w-6 h-6" />
                    <span className="font-medium tracking-wide">{item.label}</span>
                  </button>
                ))}
                <div className="border-t border-white/5 my-4" />
                <button onClick={() => { onOpenReport(); onToggleSidebar(); }} className="w-full flex items-center gap-4 px-4 py-4 rounded-xl text-red-400 hover:bg-red-500/10 border border-transparent hover:border-red-500/30 transition-all">
                  <ShieldAlert className="w-6 h-6" />
                  <span className="font-bold">Report Hazard</span>
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* MAIN CONTENT AREA */}
      <main className="flex-1 flex flex-col relative z-10 h-full overflow-hidden">

        {/* MOBILE HEADER - Hidden in Immersive Modes */}
        {currentView !== ViewState.COMMAND_CENTER && (
          <header className="h-16 px-4 flex items-center justify-between border-b border-white/5 bg-[#020817]/80 backdrop-blur-md md:hidden shrink-0">
            <button onClick={onToggleSidebar} className="p-2 -ml-2 rounded-lg hover:bg-white/5 text-gray-300">
              <Menu className="w-6 h-6" />
            </button>
            <div className="flex items-center gap-2">
              <Hexagon className="w-5 h-5 text-blue-500 fill-blue-500/20 animate-pulse" />
              <span className="font-display font-bold tracking-wider text-lg">Protectly</span>
            </div>
            <div className="w-8" /> {/* Spacer */}
          </header>
        )}

        {/* CONTENT CONTAINER */}
        <div className="flex-1 relative w-full h-full overflow-hidden">
          {children}
        </div>

      </main>

    </div>
  );
};

export default Layout;
